import { ApplicableFilter } from '../../services/types/services'

const PRESET_GROUP_PREFIX = 'preset-'

type BuildGuidedNbaTileUrlParams = {
  sourceUrl: string
  destinationUrl: string
  sourcePreAppliedFilters: (ApplicableFilter | Partial<ApplicableFilter>)[]
  destinationFilters: ApplicableFilter[]
}

const toUrlGroup = (group: string) =>
  group.startsWith(PRESET_GROUP_PREFIX)
    ? group.slice(PRESET_GROUP_PREFIX.length)
    : group

const encodeFilterSegment = (
  filter: ApplicableFilter | Partial<ApplicableFilter>,
) => `${toUrlGroup(filter.parent ?? '')}:${filter.id ?? ''}`

const buildUrlWithMergedQuery = (
  sourceUrl: string,
  destinationUrl: string,
  paramsToSet: Record<string, string>,
) => {
  const sourceQuery = sourceUrl.split('?')[1] ?? ''
  const destinationPath = destinationUrl.split('?')[0]
  const params = new URLSearchParams(sourceQuery)
  params.delete('filters')
  Object.entries(paramsToSet).forEach(([key, value]) => params.set(key, value))

  return `${destinationPath}?${params}`
}

const getPathname = (url: string) => url.split('?')[0]

const getAdditionalFilters = ({
  sourceUrl,
  destinationUrl,
  sourcePreAppliedFilters,
  destinationFilters,
}: BuildGuidedNbaTileUrlParams): (
  | ApplicableFilter
  | Partial<ApplicableFilter>
)[] => {
  if (getPathname(sourceUrl) === getPathname(destinationUrl)) {
    const destinationFiltersExcludingPreApplied = destinationFilters.filter(
      filter =>
        !sourcePreAppliedFilters.some(
          preAppliedFilter => preAppliedFilter.id === filter.id,
        ),
    )
    return destinationFiltersExcludingPreApplied
  }
  const destinationFilterIds = new Set(destinationFilters.map(({ id }) => id))

  return sourcePreAppliedFilters.filter(
    ({ id }) => !destinationFilterIds.has(id ?? ''),
  )
}

export const buildGuidedNbaTileUrl = (
  params: BuildGuidedNbaTileUrlParams,
): string => {
  const additionalFilters = getAdditionalFilters(params)
  const paramsToSet = {
    guidedNba: 'true',
    ...(additionalFilters.length > 0 && {
      filters: additionalFilters.map(encodeFilterSegment).join('.'),
    }),
  }

  return buildUrlWithMergedQuery(
    params.sourceUrl,
    params.destinationUrl,
    paramsToSet,
  )
}
